import { useState } from "react";
import { TextInput } from "risa-oasis-ui_v2";
import ChevronDownSm from "../../../../../svg/chevron-down-sm";
import { Delete } from "../../../../../svg/delete";
import { BiosimilarFamily } from "../types";
import { isDoseConvertedFamily } from "../utils/biosimilarConfigHelpers";
import CollapsibleSection from "./CollapsibleSection";
import DoseConversionsSection from "./DoseConversionsSection";
import MemberTagsSection from "./MemberTagsSection";

/**
 * Props for one expandable family card.
 */
interface BiosimilarFamilyCardProps {
  /** Stable index in the config `families` array. */
  familyIndex: number;
  /** Family definition from the config document. */
  family: BiosimilarFamily;
  /** Applies updates to this family in the parent array. */
  onUpdate: (patch: Partial<BiosimilarFamily>) => void;
  /** Removes this family from the config. */
  onDelete: () => void;
}

/**
 * Expandable card for one drug family with editable members and dose conversions.
 *
 * @param props - Family data and handlers
 * @returns Family card matching the drug family configuration design
 */
const BiosimilarFamilyCard = ({
  familyIndex,
  family,
  onUpdate,
  onDelete,
}: BiosimilarFamilyCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const members = family.members ?? [];
  const doseGroups = Object.entries(family.dose_groups ?? {});
  const doseConverted = isDoseConvertedFamily(family);

  const handleDoseGroupChange = (group: string, nextMembers: string[]) => {
    onUpdate({
      dose_groups: { ...(family.dose_groups ?? {}), [group]: nextMembers },
    });
  };

  return (
    <div className="rounded-lg border border-primaryGray-16 bg-white">
      <div
        className="flex cursor-pointer flex-row items-center gap-3 px-4 py-3"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span
          className={`flex text-primaryGray-7 transition-transform duration-300 ease-in-out ${
            expanded ? "rotate-0" : "-rotate-90"
          }`}
        >
          <ChevronDownSm />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-row items-center gap-2">
            <div className="truncate text-h12 font-semibold text-primaryGray-1">
              {family.family?.trim() || "Untitled family"}
            </div>
            {doseConverted ? (
              <span className="rounded bg-[#FFF4E5] px-2 py-0.5 text-x-tiny font-semibold uppercase text-[#B54708]">
                Dose converted
              </span>
            ) : null}
          </div>
          {!expanded ? (
            <div className="truncate text-tiny text-primaryGray-7">
              {(members.length > 0
                ? members
                : doseGroups.flatMap(([, groupMembers]) => groupMembers)
              ).join("   ") || "No members"}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label="Delete family"
        >
          <Delete height={14} width={14} />
        </button>
      </div>

      <CollapsibleSection expanded={expanded}>
        <div className="flex flex-col gap-4 border-t border-primaryGray-16 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              id={`family-name-${familyIndex}`}
              label="Family name"
              defaultValue={family.family ?? ""}
              onChange={(event) => onUpdate({ family: event.value })}
            />
            {family.type === "dose_converted" || family.original_drug ? (
              <TextInput
                id={`original-drug-${familyIndex}`}
                label="Original drug"
                defaultValue={family.original_drug ?? ""}
                onChange={(event) => onUpdate({ original_drug: event.value })}
              />
            ) : null}
          </div>

          {family.type === "dose_grouped" ? (
            doseGroups.map(([group, groupMembers]) => (
              <MemberTagsSection
                key={group}
                title={group}
                rowId={`family-${familyIndex}-group-${group}`}
                members={groupMembers}
                onChange={(next) => handleDoseGroupChange(group, next)}
              />
            ))
          ) : (
            <MemberTagsSection
              title="Members"
              rowId={`family-${familyIndex}-members`}
              members={members}
              onChange={(next) => onUpdate({ members: next })}
            />
          )}

          {family.type !== "dose_grouped" ? (
            <DoseConversionsSection family={family} onUpdate={onUpdate} />
          ) : null}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default BiosimilarFamilyCard;
